import type { DownloadEvent } from "../data-model/events.js";
import type { Logger } from "../utils/logger.js";

const PROGRESS_EMIT_INTERVAL_MS = 1000;

/**
 * Per-download event throttle + dispatch. NOTE: stateful (progress/toc
 * throttle); construct a fresh EventBus per download() call — never reuse.
 */
export class EventBus {
  readonly #listener: ((e: DownloadEvent) => void) | undefined;
  readonly #logger: Logger | undefined;
  #lastProgressMs = 0;
  #lastTocDiscovered = -1;

  constructor(listener?: (e: DownloadEvent) => void, logger?: Logger) {
    this.#listener = listener;
    this.#logger = logger;
  }

  emit(event: DownloadEvent): void {
    if (!this.#listener) return;

    if (event.type === "progress") {
      const now = Date.now();
      // A zero-chapter download never emits a `progress` event — #fetchChapter
      // only emits on a chapter success. (isFinal here just bypasses the 1s
      // throttle for the terminal emit; it is not what suppresses zero-total.)
      const isFinal = event.total > 0 && event.completed >= event.total;
      if (!isFinal && now - this.#lastProgressMs < PROGRESS_EMIT_INTERVAL_MS) return;
      this.#lastProgressMs = now;
    }

    if (event.type === "toc:progress") {
      if (event.discovered === this.#lastTocDiscovered) return;
      this.#lastTocDiscovered = event.discovered;
    }

    try {
      this.#listener(event);
    } catch (err) {
      this.#logger?.warn({ err, eventType: event.type }, "event listener threw; download continues");
    }
  }
}
